import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Textarea } from '@/components/form/Textarea';
import { useToast } from '@/components/ui/useToast';
import { useDocViewer } from '@/components/ui/useDocViewer';
import { getGatePass, markDelivered } from '@/api/gatePass';
import { formatDate } from '@/lib/format';
import { apiError } from '@/lib/apiError';
import { orderRef } from '@/lib/orderRef';

interface Props {
  open: boolean;
  gpId: number | null;
  onClose: () => void;
}

export function GatePassDetailModal({ open, gpId, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const doc = useDocViewer();
  const [showDeliver, setShowDeliver] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState('');
  const [remarks, setRemarks] = useState('');

  const query = useQuery({
    queryKey: ['gate-pass', gpId],
    queryFn: () => getGatePass(gpId as number),
    enabled: open && !!gpId,
  });
  const gp = query.data ?? null;

  useEffect(() => {
    if (open) {
      setShowDeliver(false);
      setDeliveryDate(new Date().toISOString().slice(0, 10));
      setRemarks('');
    }
  }, [open]);

  const deliverMut = useMutation({
    mutationFn: () =>
      markDelivered(gpId as number, { deliveryDate, remarks }),
    onSuccess: (r) => {
      toast.pushStep(`${r.gpNo} delivered — enquiry fulfilled`, {
        body: 'The dispatch is complete. Track it on the Fulfillment page.',
        href: '/sales/fulfillment',
        nextLabel: 'Open Fulfillment',
        tag: `gp-delivered-${r.id}`,
      });
      qc.invalidateQueries({ queryKey: ['gate-passes'] });
      qc.invalidateQueries({ queryKey: ['gate-pass', gpId] });
      qc.invalidateQueries({ queryKey: ['fulfillment'] });
      qc.invalidateQueries({ queryKey: ['enquiries'] });
      onClose();
    },
    onError: (e) => {
      toast.push(apiError(e, 'Failed'), 'error');
    },
  });

  if (!open) return null;

  return (
    <Modal
      open={open}
      title={gp ? `Gate Pass ${gp.gpNo}` : 'Loading…'}
      onClose={onClose}
      size="lg"
      footer={
        gp ? (
          <>
            <Button variant="glass" onClick={onClose}>
              Close
            </Button>
            <Button
              variant="glass"
              onClick={() => doc.open('gate-pass', gp.id, gp.gpNo)}
            >
              View Doc
            </Button>
            {gp.status === 'Active' && (
              <Button variant="primary" onClick={() => setShowDeliver(true)}>
                Mark Delivered
              </Button>
            )}
          </>
        ) : null
      }
    >
      {query.isLoading && <div className="p-4 text-sm">Loading…</div>}
      {gp && (
        <div className="space-y-3 text-xs">
          <div className="grid grid-cols-4 gap-3">
            <KV
              label="Status"
              value={
                <Badge tone={gp.status === 'Closed' ? 'success' : 'accent'}>
                  {gp.status}
                </Badge>
              }
            />
            <KV
              label="Delivery"
              value={
                <Badge
                  tone={
                    gp.deliveryStatus === 'Delivered'
                      ? 'success'
                      : gp.deliveryStatus === 'In Transit'
                        ? 'accent'
                        : 'muted'
                  }
                >
                  {gp.deliveryStatus ?? '—'}
                </Badge>
              }
            />
            <KV label="GP Date" value={formatDate(gp.gpDate)} />
            <KV
              label="Exit"
              value={gp.exitTimestamp ? new Date(gp.exitTimestamp).toLocaleString('en-IN') : '—'}
            />
            <KV label="Customer" value={gp.party?.name ?? '—'} />
            <KV label="Invoice" value={gp.invoice?.invoiceNo ?? '—'} />
            <KV label="DO" value={gp.dispatchOrder?.doNo ?? '—'} />
            <KV label="Order Ref" value={orderRef(gp.dispatchOrder?.enquiry)} />
            <KV label="Vehicle" value={gp.vehicleNo ?? '—'} />
            <KV label="Driver" value={gp.driverName ?? '—'} />
            <KV label="Transporter" value={gp.transporterName ?? '—'} />
            <KV label="LR / E-way" value={`${gp.lrNo ?? '—'} / ${gp.ewayBillNo ?? '—'}`} />
            <KV label="Seal #" value={gp.sealLockNo ?? '—'} />
            <KV label="Gate Officer" value={gp.gateOfficer ?? '—'} />
            <KV label="Boxes / Weight" value={`${gp.boxCount} · ${gp.grossWeightKg} kg`} />
            <KV
              label="Returnable"
              value={
                gp.isReturnable
                  ? `Yes — by ${formatDate(gp.expectedReturnDate)}`
                  : 'No (one-way)'
              }
            />
          </div>

          {gp.itemsSummary && (
            <div className="rounded-md border border-[var(--border-soft)] bg-[var(--bg-soft)] p-2">
              <div className="text-[10px] font-bold uppercase text-[var(--text-muted)]">
                Items Summary
              </div>
              <div className="mt-1 text-[12px]">{gp.itemsSummary}</div>
            </div>
          )}

          {gp.status === 'Closed' && gp.deliveryStatus === 'Delivered' && (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-emerald-900">
              <div className="text-[10px] font-bold uppercase">Delivered</div>
              <div className="mt-1 text-[12px]">
                On <strong>{formatDate(gp.deliveryDate)}</strong> —{' '}
                {gp.deliveryRemarks ?? '—'}
              </div>
            </div>
          )}

          {showDeliver && (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3">
              <div className="mb-2 text-[10px] font-bold uppercase text-emerald-700">
                Mark Delivered — closes enquiry stage
              </div>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                <FormGroup label="Delivery Date">
                  <Input
                    type="date"
                    value={deliveryDate}
                    onChange={(e) => setDeliveryDate(e.target.value)}
                  />
                </FormGroup>
                <FormGroup label="Remarks">
                  <Textarea
                    rows={2}
                    placeholder="POD #, receiver name, condition…"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                  />
                </FormGroup>
              </div>
              <div className="mt-2 flex justify-end gap-2">
                <Button variant="glass" size="sm" onClick={() => setShowDeliver(false)}>
                  Back
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => deliverMut.mutate()}
                  disabled={!remarks.trim() || deliverMut.isPending}
                >
                  Confirm Delivered
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-[var(--text-muted)]">{label}</div>
      <div className="text-[12px] font-semibold">{value}</div>
    </div>
  );
}
